/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { InitiativePlain, InitiativeType } from '../types/initiative';
import { ChildrenTree, Tree } from '../types/tree';
import { createSelector } from '@reduxjs/toolkit';
import { SafeUser } from '@g17eco/types/user';
import { GlobalDataState } from '../reducers/global-data';
import { CurrentUserState } from '../reducers/current-user';
import { GenericState } from '../slice/slice';

type RootMap = Map<string, RootMapItem>;

interface State {
  globalData: GlobalDataState;
  portfolioTree: {
    data: InitiativePlain[];
  };
  currentUser: CurrentUserState;
}

export const getInitiativeTreeData = (state: State) =>
  state.globalData.loaded ? state.globalData.data.initiativeTree : undefined;
const getPortfolioTree = (state: State) => state.portfolioTree;
const getUserState = (state: State) => state.currentUser;

export const getInitiativeGroupFlatTree = createSelector([getPortfolioTree], (treeState) => treeState.data);

export const getInitiativeTypeTree = createSelector([getInitiativeTreeData], (initiativeTree?: InitiativePlain[]) => {
  if (!initiativeTree) {
    return [];
  }
  return initiativeTree.filter((initiative) => initiative.type !== InitiativeType.initiativeGroup);
});

const getRootInitiatives = (user: SafeUser, initiativeTree: InitiativePlain[]): InitiativePlain[] => {
  if (!user.permissions) {
    return [];
  }

  // Ensure we have unique root initiatives
  const initiatives = new Map<string, InitiativePlain>();
  for (const { initiativeId } of user.permissions) {
    const initiative = initiativeTree.find((d) => d._id === initiativeId);
    if (initiative) {
      initiatives.set(initiative._id, initiative);
    }
  }
  return Array.from(initiatives.values());
};

const sortByName = (a: ChildrenTree, b: ChildrenTree) => {
  if (a.name < b.name) return -1;
  if (a.name > b.name) return 1;
  return 0;
};

const getRecursiveChildrenByParentId = (
  tree: InitiativePlain[],
  parentId: string | undefined,
  rootMap: RootMap,
  existingCount: number = 0,
): ChildrenTree[] => {
  const structureUsage = 'initiative_structure';
  return tree
    .filter((d) => {
      if (d.usage && d.usage.length > 0 && !d.usage.includes(structureUsage)) {
        return false;
      }
      // Match or it's duplicate children
      return d.parentId === parentId || (typeof d.parentId === 'string' && parentId?.startsWith(d.parentId));
    })
    .map((initiative) => {
      let id = initiative._id;
      const existingNode = rootMap.get(id);

      if (existingNode) {
        id = `${existingNode.id}_${existingNode.count}`;
        existingCount++;
      }

      // Keep track of all the  nodes
      rootMap.set(id, { id: id, name: initiative.name, count: existingCount });

      return {
        id: id,
        code: initiative.code,
        name: initiative.name,
        type: initiative.type,
        tags: initiative.tags,
        permissionGroup: initiative.permissionGroup,
        disabled: initiative.usage && !initiative.usage.includes(structureUsage),
        children: getRecursiveChildrenByParentId(tree, id, rootMap, existingCount),
      };
    })
    .sort(sortByName);
};

export interface RootMapItem {
  id: string;
  name: string;
  count: number;
}

export const createEmptyTree = (): Tree => ({
  id: '',
  name: '',
  code: '',
  disabled: false,
  loaded: false,
  children: [],
});

const createInitiativeTree = (initiativeTree: InitiativePlain[], currentUser: CurrentUserState) => {
  const tree: Tree = createEmptyTree();

  if (initiativeTree.length === 0 || !currentUser.loaded) {
    return tree;
  }

  tree.loaded = true;
  const initiatives = getRootInitiatives(currentUser.data, initiativeTree);

  const rootMap = new Map<string, RootMapItem>();
  initiatives.forEach((i) =>
    rootMap.set(i._id, {
      id: i._id,
      name: i.name,
      count: 0,
    }),
  );

  for (const initiative of initiatives) {
    if (initiative.usage && initiative.usage.length > 0 && !initiative.usage.includes('initiative_structure')) {
      continue;
    }
    tree.children.push({
      id: initiative._id,
      code: initiative.code,
      name: initiative.name,
      type: initiative.type,
      tags: initiative.tags,
      permissionGroup: initiative.permissionGroup,
      disabled: initiative.usage && !initiative.usage.includes('initiative_structure'),
      children: getRecursiveChildrenByParentId(initiativeTree, initiative._id, rootMap),
    });
  }
  tree.children.sort(sortByName);
  return tree;
};

export const getInitiativeTree = createSelector([getUserState, getInitiativeTypeTree], (userState, initiativeTree) => {
  return createInitiativeTree(initiativeTree, userState);
});

const MAX_DEPTH = 5;
const getRecursiveChildrenByInitiativeGroup = (
  tree: InitiativePlain[],
  parentId: string | undefined,
  rootMap: RootMap,
  existingCount: number = 0,
  depth = 1,
): ChildrenTree[] => {
  if (depth >= MAX_DEPTH) {
    return [];
  }

  const groupIds =
    tree.find((initiative) => initiative._id === parentId)?.initiativeGroup?.group.map((g) => g.initiativeId) ?? [];

  const childGroups = tree.filter((d) => {
    // Look at initiativeGroup
    if (d.type === InitiativeType.initiativeGroup && groupIds.includes(d._id)) {
      return true;
    }
    // Match or it's duplicate children
    return d.parentId === parentId || (typeof d.parentId === 'string' && parentId?.startsWith(d.parentId));
  });

  const group = childGroups.map((initiative) => {
    let id = initiative._id;
    const existingNode = rootMap.get(id);
    if (existingNode) {
      id = `${existingNode.id}_${existingNode.count}`;
      existingCount++;
    }
    // Keep track of all the  nodes
    rootMap.set(id, { id: id, name: initiative.name, count: existingCount });

    return {
      id: id,
      code: initiative.code,
      name: initiative.name,
      type: initiative.type,
      disabled: false, // Filtered out these in filter phase
      children: getRecursiveChildrenByInitiativeGroup(tree, id, rootMap, existingCount, depth + 1),
    };
  });
  return group.sort(sortByName);
};

export const createTree = (initiativeTree: InitiativePlain[], currentUser: GenericState<SafeUser>) => {
  const tree: Tree = createEmptyTree();
  if (initiativeTree.length === 0 || !currentUser.loaded) {
    return tree;
  }

  tree.loaded = true;
  const routeGroups: string[] = currentUser.data.permissions?.map((p) => p.initiativeId) ?? [];
  const rootInitiatives = initiativeTree.filter((i) => {
    return i.type === InitiativeType.initiativeGroup && routeGroups.includes(i._id);
  });

  const rootMap = new Map<string, RootMapItem>();
  rootInitiatives.forEach((i) =>
    rootMap.set(i._id, {
      id: i._id,
      name: i.name,
      count: 0,
    }),
  );

  for (const initiative of rootInitiatives) {
    tree.children.push({
      id: initiative._id,
      code: initiative.code,
      name: initiative.name,
      type: initiative.type,
      disabled: false,
      children: getRecursiveChildrenByInitiativeGroup(initiativeTree, initiative._id, rootMap),
    });
  }
  tree.children.sort(sortByName);
  return tree;
};

export const getInitiativeGroupTree = createSelector(
  [getUserState, getInitiativeGroupFlatTree],
  (currentUser, initiativeTree) => {
    return currentUser.loaded ? createTree(initiativeTree, currentUser) : createEmptyTree();
  },
);

export const archivedRoot = {
  _id: 'archived-root',
  code: 'archived-root',
  name: 'Archived',
  type: InitiativeType.initiative,
};

export const getRecursiveArchivedChildrenByParentId = (
  tree: InitiativePlain[],
  parentId: string | undefined,
  rootMap: RootMap,
  existingCount: number = 0,
): ChildrenTree[] => {
  return tree
    .filter((d) => {
      // Match or it's duplicate children
      return d.parentId === parentId || (typeof d.parentId === 'string' && parentId?.startsWith(d.parentId));
    })
    .map((initiative) => {
      let id = initiative._id;
      const existingNode = rootMap.get(id);

      if (existingNode) {
        id = `${existingNode.id}_${existingNode.count}`;
        existingCount++;
      }

      // Keep track of all the  nodes
      rootMap.set(id, { id: id, name: initiative.name, count: existingCount });

      return {
        id: id,
        code: initiative.code,
        name: initiative.name,
        type: initiative.type,
        parentId: initiative.parentId,
        tags: initiative.tags,
        disabled: false,
        children: getRecursiveChildrenByParentId(tree, id, rootMap, existingCount),
      };
    })
    .sort(sortByName);
};

export const createArchivedInitiativeTree = ({
  archivedInitiatives,
  existingInitiatives,
}: {
  archivedInitiatives: InitiativePlain[] | undefined;
  existingInitiatives: InitiativePlain[];
}) => {
  const tree: Tree = createEmptyTree();

  if (!archivedInitiatives || archivedInitiatives.length === 0) {
    return tree;
  }

  tree.loaded = true;
  const existingIds = existingInitiatives?.map((i) => i._id);

  const rootMap = new Map<string, RootMapItem>();
  rootMap.set(archivedRoot._id, {
    id: archivedRoot._id,
    name: archivedRoot.name,
    count: 0,
  });

  // tracing back to the old parent id
  const oldParentIds = Array.from(
    archivedInitiatives.reduce((acc, initiative) => {
      if (initiative.parentId && existingIds.includes(initiative.parentId)) {
        acc.add(initiative.parentId);
      }
      return acc;
    }, new Set<string>()),
  );

  const children = oldParentIds
    .reduce((acc, parentId) => {
      return acc.concat(getRecursiveArchivedChildrenByParentId(archivedInitiatives, parentId, rootMap));
    }, [] as ChildrenTree[])
    .sort(sortByName);

  tree.children.push({
    id: archivedRoot._id,
    code: archivedRoot.code,
    name: archivedRoot.name,
    type: archivedRoot.type,
    disabled: true,
    children,
  });

  tree.children.sort(sortByName);
  return tree;
};

export const getInitiativesTreeMapById = createSelector(
  [getInitiativeTreeData],
  (initiativeTree?: InitiativePlain[]) => {
    const treeMap = new Map<string, InitiativePlain>();
    if (!initiativeTree) {
      return treeMap;
    }

    initiativeTree.forEach((i) => treeMap.set(i._id, i));

    return treeMap;
  },
);
